import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import {
    templateAuthorContentfulRelayFragment$data,
    templateAuthorContentfulRelayFragment$key
} from "@/__generated_contentful__/templateAuthorContentfulRelayFragment.graphql";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";

interface TemplateAuthorProps {
    template: templateAuthorContentfulRelayFragment$key;
}

const templateAuthor = css({
    fontSize: "0.75rem",
    lineHeight: "1.3125rem",
    fontWeight: 500,
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#0052CC",
    // FIXME: GWP-3063: Disabling instead of fixing so we don't deviate from the control visually.
    // eslint-disable-next-line local/ensure-wac-token-usage
    fontFamily: "var(--bxp-charlie-display-font-family)",
    margin: 0,
    marginBlockEnd: "24px",
    textTransform: "uppercase"
});

const isAuthor = (
    contributor: templateAuthorContentfulRelayFragment$data["contributor"]
) => contributor?.__typename === "Author";

const isOrganization = (
    contributor: templateAuthorContentfulRelayFragment$data["contributor"]
) => contributor?.__typename === "CompanyNameInternal";

const TemplateAuthorRelayFragment = graphql`
    fragment templateAuthorContentfulRelayFragment on ProductTemplate {
        contributor {
            __typename
            ... on Author {
                authorNameExternal
                jobTitle
                organization {
                    organizationNameExternal
                }
            }
            ... on CompanyNameInternal {
                organizationNameExternal
            }
        }
    }
`;

export const TemplateAuthor = ({ template }: TemplateAuthorProps) => {
    const { contributor } = useFragment(TemplateAuthorRelayFragment, template);

    let contributorName =
        (isAuthor(contributor) && contributor.authorNameExternal) ||
        (isOrganization(contributor) && contributor.organizationNameExternal) ||
        undefined;

    const author = isAuthor(contributor) ? contributor : null;

    let authorDetail = author?.jobTitle;
    if (authorDetail && author?.organization?.organizationNameExternal) {
        authorDetail = `${authorDetail} @ ${author.organization.organizationNameExternal}`;
    }

    if (authorDetail) {
        contributorName = `${contributorName}, (${authorDetail})`;
    }

    return (
        contributorName && (
            <h3 css={templateAuthor}>
                <FormattedMessage
                    {...ConfluenceTemplateMessages.templateAuthorByLine}
                    values={{
                        author: contributorName
                    }}
                />
            </h3>
        )
    );
};
